// frontend/src/components/ClientForm.js
// Componente de formulario para crear o editar un cliente.

import React, { useState, useEffect } from 'react';
import { useClientStore } from '../store/clientStore';
import { useUIStore } from '../store/uiStore';
import LoadingSpinner from './LoadingSpinner';

/**
 * @component ClientForm
 * @description Formulario reutilizable para añadir o editar clientes.
 * @param {object} props - Propiedades del componente.
 * @param {object|null} [props.clientToEdit=null] - Objeto cliente si se está editando, null para añadir.
 * @param {function} props.onSuccess - Callback que se ejecuta al enviar el formulario con éxito.
 * @param {function} props.onCancel - Callback que se ejecuta al cancelar el formulario.
 */
function ClientForm({ clientToEdit = null, onSuccess, onCancel }) {
  // Estado local para los campos del formulario (solo campos básicos del cliente)
  const [formData, setFormData] = useState({
    organizationName: '',
    fantasyName: '',
    opportunityNumber: '',
    accountManager: '',
    solutionSales: '',
  });

  // Acciones y estado del store de clientes y UI
  const { addClient, updateClient } = useClientStore();
  const { isLoading, setIsLoading } = useUIStore();

  // Carga los datos del cliente a editar cuando el componente se monta o clientToEdit cambia
  useEffect(() => {
    if (clientToEdit) {
      setFormData({
        organizationName: clientToEdit.organizationName || '',
        fantasyName: clientToEdit.fantasyName || '',
        opportunityNumber: clientToEdit.opportunityNumber || '',
        accountManager: clientToEdit.accountManager || '',
        solutionSales: clientToEdit.solutionSales || '',
      });
    } else {
      // Limpia el formulario si no hay cliente a editar
      setFormData({
        organizationName: '',
        fantasyName: '',
        opportunityNumber: '',
        accountManager: '',
        solutionSales: '',
      });
    }
  }, [clientToEdit]);

  // Maneja cambios en los inputs del formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Maneja el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (clientToEdit) {
        await updateClient(clientToEdit.id, formData);
      } else {
        await addClient(formData);
      }
      onSuccess(); // Llama al callback de éxito
    } catch (error) {
      // Los errores ya se manejan en el store de UI
    } finally {
      setIsLoading(false);
    }
  };

  const formTitle = clientToEdit ? 'Editar Cliente' : 'Añadir Nuevo Cliente';
  const submitButtonText = clientToEdit ? 'Guardar Cambios' : 'Añadir Cliente';

  return (
    <div className="p-6 bg-white rounded-lg shadow-xl w-full max-w-2xl mx-auto my-8">
      <h2 className="text-3xl font-extrabold text-gray-900 mb-6 text-center">{formTitle}</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Campos obligatorios del cliente */}
          <div>
            <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700">Nombre de la Organización <span className="text-red-500">*</span></label>
            <input type="text" id="organizationName" name="organizationName" value={formData.organizationName} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
          </div>
          <div>
            <label htmlFor="fantasyName" className="block text-sm font-medium text-gray-700">Nombre de Fantasía</label>
            <input type="text" id="fantasyName" name="fantasyName" value={formData.fantasyName} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
          </div>
          <div>
            <label htmlFor="opportunityNumber" className="block text-sm font-medium text-gray-700">Nº de Oportunidad (opcional, ej: LATAM-11388)</label>
            <input type="text" id="opportunityNumber" name="opportunityNumber" value={formData.opportunityNumber} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
          </div>
          <div>
            <label htmlFor="accountManager" className="block text-sm font-medium text-gray-700">Account Manager <span className="text-red-500">*</span></label>
            <input type="text" id="accountManager" name="accountManager" value={formData.accountManager} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
          </div>
          <div>
            <label htmlFor="solutionSales" className="block text-sm font-medium text-gray-700">Solution Sales <span className="text-red-500">*</span></label>
            <input type="text" id="solutionSales" name="solutionSales" value={formData.solutionSales} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
          </div>
        </div>

        {/* Botones de acción */}
        <div className="pt-6 flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={`px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isLoading}
          >
            {isLoading ? <LoadingSpinner size="sm" color="text-white" /> : submitButtonText}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ClientForm;
